import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Modal } from '../../components/ui/Modal'
import { Field } from '../../components/ui/Field'
import { Input, Select } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { PermissionGate } from '../../components/PermissionGate'
import { automationApi } from '../../api/automation.api'
import { tasksApi } from '../../api/tasks.api'
import { toast } from '../../store/toastStore'
import { ApiError } from '../../api/client'
import { ActionParamsFields } from './ActionParamsFields'
import { TRIGGER_LABELS, ACTION_LABELS, OP_LABELS, CONDITION_FIELD_SUGGESTIONS } from './automationLabels'
import type { AutomationAction, AutomationCondition, AutomationRule, TriggerType, ActionType } from '../../api/types'
import formStyles from '../../components/ui/Form.module.css'

const OP_OPTIONS = Object.keys(OP_LABELS) as AutomationCondition['op'][]
const TRIGGER_OPTIONS = Object.keys(TRIGGER_LABELS) as TriggerType[]
const ACTION_OPTIONS = Object.keys(ACTION_LABELS) as ActionType[]

export function AutomationRuleFormModal({ projectId, rule, onClose }: { projectId: string; rule?: AutomationRule | null; onClose: () => void }) {
  const isEdit = Boolean(rule)
  const queryClient = useQueryClient()

  const [name, setName] = useState(rule?.name ?? '')
  const [enabled, setEnabled] = useState(rule?.enabled ?? true)
  const [triggerType, setTriggerType] = useState<TriggerType>(rule?.triggerType ?? 'label.added')
  const [conditions, setConditions] = useState<AutomationCondition[]>(rule?.conditions ?? [])
  const [actions, setActions] = useState<AutomationAction[]>(rule?.actions ?? [{ type: 'send_panel_notification', params: {} }])
  const [error, setError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [testTaskId, setTestTaskId] = useState('')
  const [testResult, setTestResult] = useState<{ conditionsMatch: boolean; results: { type: string; success: boolean; detail: string; error?: string }[] } | null>(null)

  const tasksQuery = useQuery({ queryKey: ['tasks', { projectId, pageSize: 200 }], queryFn: async () => (await tasksApi.list({ projectId, pageSize: 200 })).data })

  const saveMutation = useMutation({
    mutationFn: () =>
      isEdit && rule
        ? automationApi.update(rule.id, { name, enabled, triggerType, conditions, actions })
        : automationApi.create({ projectId, name, enabled, triggerType, conditions, actions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation'], exact: false })
      toast.success(isEdit ? 'Regel aktualisiert.' : 'Regel angelegt.')
      onClose()
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.'),
  })

  const deleteMutation = useMutation({
    mutationFn: () => automationApi.remove(rule!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['automation'], exact: false })
      toast.success('Regel gelöscht.')
      onClose()
    },
  })

  const dryRunMutation = useMutation({
    mutationFn: () => automationApi.dryRun(rule!.id, testTaskId),
    onSuccess: (res) => setTestResult(res.data),
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Testlauf fehlgeschlagen.'),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    saveMutation.mutate()
  }

  const updateCondition = (index: number, patch: Partial<AutomationCondition>) => {
    setConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)))
  }
  const updateAction = (index: number, patch: Partial<AutomationAction>) => {
    setActions((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)))
  }

  return (
    <Modal
      title={isEdit ? `Regel bearbeiten — ${rule?.name}` : 'Neue Automation'}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {isEdit ? (
            <PermissionGate anyOf={['automation.manage']}>
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                Löschen
              </Button>
            </PermissionGate>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onSubmit} loading={saveMutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {error ? <div style={{ color: 'var(--danger)', fontSize: 'var(--text-sm)' }}>{error}</div> : null}

        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <div style={{ flex: 1 }}>
            <Field label="Name" htmlFor="rule-name">
              <Input id="rule-name" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            </Field>
          </div>
          <label className={formStyles.checkboxRow} style={{ alignSelf: 'flex-end', marginBottom: 8 }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Aktiv
          </label>
        </div>

        <Field label="WENN (Trigger)" htmlFor="rule-trigger">
          <Select id="rule-trigger" value={triggerType} onChange={(e) => setTriggerType(e.target.value as TriggerType)}>
            {TRIGGER_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {TRIGGER_LABELS[t]}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="UND (Bedingungen, optional)">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {conditions.map((condition, index) => (
              <div key={index} style={{ display: 'flex', gap: 6 }}>
                <Input
                  list="condition-fields"
                  placeholder="Feld, z.B. task.priority"
                  value={condition.field}
                  onChange={(e) => updateCondition(index, { field: e.target.value })}
                />
                <Select value={condition.op} onChange={(e) => updateCondition(index, { op: e.target.value as AutomationCondition['op'] })} style={{ maxWidth: 160 }}>
                  {OP_OPTIONS.map((op) => (
                    <option key={op} value={op}>
                      {OP_LABELS[op]}
                    </option>
                  ))}
                </Select>
                <Input
                  placeholder="Wert"
                  value={typeof condition.value === 'string' ? condition.value : JSON.stringify(condition.value ?? '')}
                  onChange={(e) => updateCondition(index, { value: e.target.value })}
                />
                <Button size="sm" variant="ghost" onClick={() => setConditions((prev) => prev.filter((_, i) => i !== index))}>
                  ✕
                </Button>
              </div>
            ))}
            <datalist id="condition-fields">
              {CONDITION_FIELD_SUGGESTIONS.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
            <Button size="sm" variant="secondary" onClick={() => setConditions((prev) => [...prev, { field: '', op: 'eq', value: '' }])} style={{ alignSelf: 'flex-start' }}>
              + Bedingung
            </Button>
          </div>
        </Field>

        <Field label="DANN (Aktionen)">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {actions.map((action, index) => (
              <div key={index} style={{ border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <Select value={action.type} onChange={(e) => updateAction(index, { type: e.target.value as ActionType, params: {} })} style={{ maxWidth: 240 }}>
                    {ACTION_OPTIONS.map((t) => (
                      <option key={t} value={t}>
                        {ACTION_LABELS[t]}
                      </option>
                    ))}
                  </Select>
                  <Button size="sm" variant="ghost" onClick={() => setActions((prev) => prev.filter((_, i) => i !== index))} style={{ marginLeft: 'auto' }}>
                    ✕
                  </Button>
                </div>
                <ActionParamsFields type={action.type} params={action.params} onChange={(params) => updateAction(index, { params })} />
              </div>
            ))}
            <Button size="sm" variant="secondary" onClick={() => setActions((prev) => [...prev, { type: 'send_panel_notification', params: {} }])} style={{ alignSelf: 'flex-start' }}>
              + Aktion
            </Button>
          </div>
        </Field>

        {isEdit ? (
          <div style={{ borderTop: '1px solid var(--surface-border)', paddingTop: 'var(--space-3)' }}>
            <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
              Testlauf (Dry-Run — verändert nichts)
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <Select value={testTaskId} onChange={(e) => setTestTaskId(e.target.value)}>
                <option value="">Aufgabe wählen…</option>
                {tasksQuery.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.title}
                  </option>
                ))}
              </Select>
              <Button size="sm" disabled={!testTaskId} loading={dryRunMutation.isPending} onClick={() => dryRunMutation.mutate()}>
                Testen
              </Button>
            </div>
            {testResult ? (
              <div style={{ marginTop: 8, fontSize: 'var(--text-sm)' }}>
                <div>Bedingungen erfüllt: {testResult.conditionsMatch ? 'Ja' : 'Nein'}</div>
                {testResult.results.map((r, i) => (
                  <div key={i} style={{ color: r.success ? 'var(--success)' : 'var(--danger)' }}>
                    {r.type}: {r.detail} {r.error ? `(${r.error})` : ''}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </form>

      {confirmDelete ? (
        <ConfirmDialog
          title="Regel löschen"
          message={`"${rule?.name}" wirklich löschen?`}
          danger
          loading={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </Modal>
  )
}
